export const buildChatListSilentRefreshStartState = () => ({
  isSilentRefresh: true,
});

export const buildChatListSilentRefreshFinishState = () => ({
  isSilentRefresh: false,
});

export const runChatListSilentRefresh = async ({
  applyState = null,
  commitPendingConversationActivityUpdates = null,
  currentPage = 1,
  fetchConversations = null,
  hasPendingConversations = null,
  isFetchingConversations = false,
} = {}) => {
  if (isFetchingConversations) {
    return { status: 'skipped-fetching' };
  }

  if (typeof applyState === 'function') {
    applyState(buildChatListSilentRefreshStartState());
  }

  let status = 'refreshed';

  try {
    if (typeof fetchConversations === 'function') {
      await fetchConversations(currentPage, true);
    } else {
      status = 'invalid';
    }

    if (
      status !== 'invalid' &&
      typeof hasPendingConversations === 'function' &&
      hasPendingConversations() &&
      typeof commitPendingConversationActivityUpdates === 'function'
    ) {
      commitPendingConversationActivityUpdates();
    }
  } catch (error) {
    status = 'failed';
  } finally {
    if (typeof applyState === 'function') {
      applyState(buildChatListSilentRefreshFinishState());
    }
  }

  return { status };
};

export const runChatListRefreshConversations = async ({
  activeAssigneeTab = null,
  activeSortBy = null,
  activeStatus = null,
  buildPreservedFilters = null,
  conversationType = '',
  currentPage = 1,
  fetchConversations = null,
  filterContactId = null,
  includeLabeledConversations = false,
  isFetchingConversations = false,
  itemsPerPage = 30,
  options = {},
  resetBulkActions = null,
  selectedAssigneeIdFilter = null,
  updateFilters = null,
} = {}) => {
  if (isFetchingConversations) {
    return { status: 'skipped-fetching' };
  }

  if (typeof resetBulkActions === 'function') {
    resetBulkActions();
  }

  try {
    if (
      options?.preserveLabel &&
      typeof buildPreservedFilters === 'function' &&
      typeof updateFilters === 'function'
    ) {
      updateFilters(
        buildPreservedFilters({
          activeAssigneeTab,
          activeSortBy,
          activeStatus,
          conversationType,
          currentPage,
          filterContactId,
          includeLabeledConversations,
          itemsPerPage,
          preserveLabel: options.preserveLabel,
          selectedAssigneeIdFilter,
        })
      );
    }

    if (typeof fetchConversations !== 'function') {
      return { status: 'invalid' };
    }

    await fetchConversations(currentPage);
    return { status: 'refreshed' };
  } catch (error) {
    return { error, status: 'failed' };
  }
};

export const runChatListPeriodicTickStart = ({
  commitPendingConversationActivityUpdates = null,
  hasPendingConversations = false,
  isFetchingConversations = false,
} = {}) => {
  if (hasPendingConversations) {
    if (typeof commitPendingConversationActivityUpdates === 'function') {
      commitPendingConversationActivityUpdates();
    }
    return { shouldContinue: false, status: 'committed-pending' };
  }

  if (isFetchingConversations) {
    return { shouldContinue: false, status: 'skipped-fetching' };
  }

  return { shouldContinue: true, status: 'continue' };
};

export const resolveChatListPollingInterval = ({
  activeInbox = null,
  conversationInbox = null,
  defaultInterval = 3600000,
  getCacheExpirationForInboxType = () => null,
  getInbox = () => null,
} = {}) => {
  const inboxId = conversationInbox || activeInbox;
  if (!inboxId || inboxId === 0) {
    return defaultInterval;
  }

  const inbox = typeof getInbox === 'function' ? getInbox(inboxId) : null;
  if (
    inbox?.channel_type &&
    typeof getCacheExpirationForInboxType === 'function'
  ) {
    getCacheExpirationForInboxType(inbox.channel_type);
  }

  return defaultInterval;
};

export const runChatListPeriodicUpdateStop = ({
  clearScheduledInterval = null,
  setUpdateInterval = null,
  updateInterval = null,
} = {}) => {
  if (!updateInterval) {
    return { status: 'skipped' };
  }

  if (typeof clearScheduledInterval !== 'function') {
    return { status: 'invalid' };
  }

  clearScheduledInterval(updateInterval);

  if (typeof setUpdateInterval === 'function') {
    setUpdateInterval(null);
  }

  return { status: 'stopped' };
};

export const resolveChatListShowConversationListAction = ({
  listInitialized = false,
  showConversationList = false,
} = {}) => {
  if (!showConversationList) {
    return 'stop-polling';
  }
  return listInitialized ? 'resume-polling' : 'initialize';
};

export const runChatListShowConversationListChange = ({
  initializeListView = null,
  listInitialized = false,
  resolveAction = resolveChatListShowConversationListAction,
  showConversationList = false,
  startPeriodicUpdate = null,
  stopPeriodicUpdate = null,
} = {}) => {
  if (typeof resolveAction !== 'function') {
    return { status: 'invalid' };
  }

  const action = resolveAction({ listInitialized, showConversationList });

  if (action === 'stop-polling') {
    if (typeof stopPeriodicUpdate !== 'function') {
      return { status: 'invalid', action };
    }
    stopPeriodicUpdate();
    return { status: 'stopped', action };
  }

  if (action === 'resume-polling') {
    if (typeof startPeriodicUpdate !== 'function') {
      return { status: 'invalid', action };
    }
    startPeriodicUpdate();
    return { status: 'resumed', action };
  }

  if (typeof initializeListView !== 'function') {
    return { status: 'invalid', action };
  }
  initializeListView();
  return { status: 'initialized', action };
};

export const classifyChatListMultiInboxTokenChange = ({
  oldToken,
  refreshToken,
} = {}) => {
  if (oldToken === undefined) {
    return 'initial';
  }
  if (refreshToken !== oldToken) {
    return 'changed';
  }
  return 'same';
};

export const buildChatListInboxOldTokenKey = (inboxId) => `${inboxId}_old`;

export const shouldChatListInvalidateInboxCache = ({
  oldToken,
  refreshToken,
} = {}) => oldToken !== refreshToken;

export const resolveChatListMultiInboxRefreshAction = ({
  currentPage = 1,
  inboxes = [],
  inboxRefreshTokens = null,
  refreshResults = [],
} = {}) => {
  const nextInboxRefreshTokens =
    inboxRefreshTokens instanceof Map ? new Map(inboxRefreshTokens) : new Map();
  let hasChanges = false;

  (Array.isArray(refreshResults) ? refreshResults : [])
    .filter(Boolean)
    .forEach(({ inbox, refreshToken }) => {
      const inboxId = inbox?.id;
      const oldToken = nextInboxRefreshTokens.get(inboxId);
      const change = classifyChatListMultiInboxTokenChange({
        oldToken,
        refreshToken,
      });
      if (change === 'same') {
        return;
      }
      nextInboxRefreshTokens.set(inboxId, refreshToken);
      if (change === 'changed') {
        hasChanges = true;
      }
    });

  const cacheInvalidationInboxIds = [];

  if (hasChanges && currentPage === 1) {
    (Array.isArray(inboxes) ? inboxes : []).forEach((inbox) => {
      const inboxId = inbox?.id;
      const refreshToken = nextInboxRefreshTokens.get(inboxId);
      const oldTokenKey = buildChatListInboxOldTokenKey(inboxId);
      const oldToken = nextInboxRefreshTokens.get(oldTokenKey);
      if (shouldChatListInvalidateInboxCache({ oldToken, refreshToken })) {
        cacheInvalidationInboxIds.push(inboxId);
        nextInboxRefreshTokens.set(oldTokenKey, refreshToken);
      }
    });
  }

  return {
    cacheInvalidationInboxIds,
    hasChanges,
    inboxRefreshTokens: nextInboxRefreshTokens,
    shouldRefresh: hasChanges && currentPage === 1,
  };
};

export const runChatListMultiInboxRefreshEffect = async ({
  clearConversationCacheForInbox = null,
  currentPage = 1,
  getRefreshToken = null,
  inboxes = [],
  inboxRefreshTokens = null,
  setInboxRefreshTokens = null,
  silentRefreshConversations = null,
} = {}) => {
  if (!Array.isArray(inboxes) || inboxes.length === 0) {
    return { status: 'skipped-no-inboxes' };
  }
  if (typeof getRefreshToken !== 'function') {
    return { status: 'invalid' };
  }

  const refreshResults = await Promise.all(
    inboxes.map(async (inbox) => {
      try {
        const { refresh_token: refreshToken } = await getRefreshToken(inbox.id);
        return { inbox, refreshToken };
      } catch (error) {
        return null;
      }
    })
  );

  const action = resolveChatListMultiInboxRefreshAction({
    currentPage,
    inboxes,
    inboxRefreshTokens,
    refreshResults,
  });

  if (typeof setInboxRefreshTokens === 'function') {
    setInboxRefreshTokens(action.inboxRefreshTokens);
  }

  if (action.shouldRefresh) {
    action.cacheInvalidationInboxIds.forEach((inboxId) => {
      if (typeof clearConversationCacheForInbox === 'function') {
        clearConversationCacheForInbox(inboxId);
      }
    });
    if (typeof silentRefreshConversations === 'function') {
      await silentRefreshConversations();
    }
    return {
      status: 'refreshed',
      cacheInvalidationInboxIds: action.cacheInvalidationInboxIds,
      inboxRefreshTokens: action.inboxRefreshTokens,
    };
  }

  return {
    status: action.hasChanges ? 'changed-off-page' : 'unchanged',
    cacheInvalidationInboxIds: action.cacheInvalidationInboxIds,
    inboxRefreshTokens: action.inboxRefreshTokens,
  };
};

export const resolveChatListSingleInboxRefreshAction = ({
  activePollingInboxId = null,
  currentPage = 1,
  lastSeenInboxId = null,
  lastSeenRefreshToken = null,
  refreshToken = null,
} = {}) => {
  if (lastSeenInboxId !== activePollingInboxId) {
    return {
      mode: 'memorize',
      lastSeenInboxId: activePollingInboxId,
      lastSeenRefreshToken: refreshToken,
    };
  }
  if (refreshToken !== lastSeenRefreshToken && currentPage === 1) {
    return {
      mode: 'refresh',
      lastSeenRefreshToken: refreshToken,
    };
  }
  return { mode: 'noop' };
};

export const runChatListSingleInboxRefreshEffect = async ({
  action = null,
  activePollingInboxId = null,
  clearConversationCacheForInbox = null,
  setLastSeenInboxId = null,
  setLastSeenRefreshToken = null,
  silentRefreshConversations = null,
} = {}) => {
  if (action?.mode === 'memorize') {
    if (typeof setLastSeenInboxId === 'function') {
      setLastSeenInboxId(action.lastSeenInboxId);
    }
    if (typeof setLastSeenRefreshToken === 'function') {
      setLastSeenRefreshToken(action.lastSeenRefreshToken);
    }
    return { status: 'memorized' };
  }

  if (action?.mode === 'refresh') {
    if (typeof setLastSeenRefreshToken === 'function') {
      setLastSeenRefreshToken(action.lastSeenRefreshToken);
    }
    if (typeof clearConversationCacheForInbox === 'function') {
      clearConversationCacheForInbox(activePollingInboxId);
    }
    if (typeof silentRefreshConversations === 'function') {
      await silentRefreshConversations();
    }
    return { status: 'refreshed' };
  }

  return { status: 'skipped' };
};

export const runChatListSingleInboxRefreshRequest = async ({
  activePollingInboxId = null,
  clearConversationCacheForInbox = null,
  currentPage = 1,
  getRefreshToken = null,
  lastSeenInboxId = null,
  lastSeenRefreshToken = null,
  setLastSeenInboxId = null,
  setLastSeenRefreshToken = null,
  silentRefreshConversations = null,
} = {}) => {
  if (typeof getRefreshToken !== 'function') {
    return { status: 'invalid' };
  }

  try {
    const { refresh_token: refreshToken } =
      await getRefreshToken(activePollingInboxId);
    const action = resolveChatListSingleInboxRefreshAction({
      activePollingInboxId,
      currentPage,
      lastSeenInboxId,
      lastSeenRefreshToken,
      refreshToken,
    });

    return runChatListSingleInboxRefreshEffect({
      action,
      activePollingInboxId,
      clearConversationCacheForInbox,
      setLastSeenInboxId,
      setLastSeenRefreshToken,
      silentRefreshConversations,
    });
  } catch (error) {
    return { status: 'failed' };
  }
};

export const isChatListMultiInboxPollingMode = (activePollingInboxId = null) =>
  !activePollingInboxId || activePollingInboxId === 0;

export const hasChatListAvailableInboxes = (inboxes = null) =>
  Array.isArray(inboxes) && inboxes.length > 0;

const readChatListValue = (value = null) =>
  typeof value === 'function' ? value() : value;

const readChatListBooleanValue = (value = false) =>
  Boolean(readChatListValue(value));

export const runChatListPeriodicRefreshTick = async ({
  activeInbox = null,
  clearConversationCacheForInbox = null,
  commitPendingConversationActivityUpdates = null,
  conversationInbox = null,
  currentPage = 1,
  getIsFetchingConversations = null,
  getRefreshToken = null,
  hasPendingConversations = false,
  inboxes = [],
  inboxRefreshTokens = null,
  isFetchingConversations = false,
  lastSeenInboxId = null,
  lastSeenRefreshToken = null,
  setInboxRefreshTokens = null,
  setLastSeenInboxId = null,
  setLastSeenRefreshToken = null,
  silentRefreshConversations = null,
} = {}) => {
  const readFetching = () =>
    typeof getIsFetchingConversations === 'function'
      ? Boolean(getIsFetchingConversations())
      : Boolean(isFetchingConversations);

  const tickStart = runChatListPeriodicTickStart({
    commitPendingConversationActivityUpdates,
    hasPendingConversations: readChatListBooleanValue(hasPendingConversations),
    isFetchingConversations: readFetching(),
  });
  if (!tickStart.shouldContinue) {
    return { status: tickStart.status };
  }

  try {
    if (readFetching()) {
      return { status: 'skipped-fetching' };
    }

    const activePollingInboxId = conversationInbox || activeInbox;

    if (isChatListMultiInboxPollingMode(activePollingInboxId)) {
      const result = await runChatListMultiInboxRefreshEffect({
        clearConversationCacheForInbox,
        currentPage,
        getRefreshToken,
        inboxes,
        inboxRefreshTokens,
        setInboxRefreshTokens,
        silentRefreshConversations,
      });
      return { mode: 'multi-inbox', status: result.status, result };
    }

    const result = await runChatListSingleInboxRefreshRequest({
      activePollingInboxId,
      clearConversationCacheForInbox,
      currentPage,
      getRefreshToken,
      lastSeenInboxId,
      lastSeenRefreshToken,
      setLastSeenInboxId,
      setLastSeenRefreshToken,
      silentRefreshConversations,
    });
    return { mode: 'single-inbox', status: result.status, result };
  } catch (error) {
    return { status: 'failed' };
  }
};

export const runChatListPeriodicUpdateStart = ({
  activeInbox = null,
  clearConversationCacheForInbox = null,
  commitPendingConversationActivityUpdates = null,
  conversationInbox = null,
  getActiveInbox = () => activeInbox,
  getCacheExpirationForInboxType = null,
  getConversationInbox = () => conversationInbox,
  getCurrentPage = () => 1,
  getInbox = null,
  getInboxes = () => [],
  getInboxRefreshTokens = () => null,
  getIsFetchingConversations = null,
  getLastSeenInboxId = () => null,
  getLastSeenRefreshToken = () => null,
  getRefreshToken = null,
  hasPendingConversations = false,
  resolvePollingInterval = resolveChatListPollingInterval,
  scheduleInterval = null,
  setInboxRefreshTokens = null,
  setLastSeenInboxId = null,
  setLastSeenRefreshToken = null,
  setUpdateInterval = null,
  silentRefreshConversations = null,
  tickRunner = runChatListPeriodicRefreshTick,
  updateInterval = null,
} = {}) => {
  if (updateInterval) {
    return { status: 'skipped' };
  }

  if (
    typeof scheduleInterval !== 'function' ||
    typeof setUpdateInterval !== 'function' ||
    typeof tickRunner !== 'function'
  ) {
    return { status: 'invalid' };
  }

  const pollingInterval = resolvePollingInterval({
    activeInbox,
    conversationInbox,
    getCacheExpirationForInboxType,
    getInbox,
  });

  const interval = scheduleInterval(async () => {
    await tickRunner({
      activeInbox: readChatListValue(getActiveInbox),
      clearConversationCacheForInbox,
      commitPendingConversationActivityUpdates,
      conversationInbox: readChatListValue(getConversationInbox),
      currentPage: readChatListValue(getCurrentPage),
      getIsFetchingConversations,
      getRefreshToken,
      hasPendingConversations,
      inboxes: readChatListValue(getInboxes),
      inboxRefreshTokens: readChatListValue(getInboxRefreshTokens),
      lastSeenInboxId: readChatListValue(getLastSeenInboxId),
      lastSeenRefreshToken: readChatListValue(getLastSeenRefreshToken),
      setInboxRefreshTokens,
      setLastSeenInboxId,
      setLastSeenRefreshToken,
      silentRefreshConversations,
    });
  }, pollingInterval);

  setUpdateInterval(interval);
  return { interval, pollingInterval, status: 'started' };
};
